import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Grid
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const ViewFaqModal = ({ open, onClose, faq }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  if (!faq) return null;

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent || '' };
  };

  const renderHTMLContent = (content, defaultValue = 'Not provided') => {
    if (!content) return defaultValue;

    // Check if content contains HTML tags
    const hasHTML = /<[a-z][\s\S]*>/i.test(content);

    if (hasHTML) {
      return (
        <Box
          sx={{
            color: palette.text.secondary,
            lineHeight: 1.6,
            fontSize: '0.95rem',
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              color: palette.text.primary,
              marginBottom: '0.5rem',
              marginTop: '0.5rem',
              fontSize: 'inherit'
            },
            '& p': {
              marginBottom: '0.5rem'
            },
            '& ul, & ol': {
              paddingLeft: '1.5rem',
              marginBottom: '0.5rem'
            },
            '& li': {
              marginBottom: '0.25rem'
            },
            '& a': {
              color: palette.primary.main,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            },
            '& strong, & b': {
              fontWeight: 600
            },
            '& em, & i': {
              fontStyle: 'italic'
            },
            '& blockquote': {
              borderLeft: `3px solid ${palette.primary.main}`,
              paddingLeft: '1rem',
              marginLeft: 0,
              fontStyle: 'italic',
              color: palette.text.secondary
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: '0.5rem',
              fontSize: '0.9rem'
            },
            '& th, & td': {
              border: `1px solid ${palette.divider}`,
              padding: '0.25rem 0.5rem',
              textAlign: 'left'
            },
            '& th': {
              backgroundColor: palette.grey[50],
              fontWeight: 600
            }
          }}
          dangerouslySetInnerHTML={createMarkup(content)}
        />
      );
    }

    // Fallback to plain text rendering
    return (
      <Typography
        variant="body1"
        sx={{
          color: palette.text.secondary,
          lineHeight: 1.6,
          fontSize: '0.95rem'
        }}
      >
        {content}
      </Typography>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFileTypeFromUrl = (url) => {
    if (!url) return '';
    const extension = url.split('.').pop()?.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];

    return imageExtensions.includes(extension)
      ? 'image'
      : videoExtensions.includes(extension)
      ? 'video'
      : 'file';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* Header - Same Theme */}
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText
          }}
        >
          FAQ Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Basic Information - 3 items per row */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                mb: 2,
                color: palette.primary.titledark
              }}
            >
              Basic Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Title
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: palette.text.secondary,
                      fontSize: '0.95rem',
                      lineHeight: 1.5
                    }}
                  >
                    {faq.title}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ 
                    fontFamily: 'poppinsSemiBold', 
                    color: palette.text.primary,
                    fontSize: '0.875rem'
                  }}>
                    Slug
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: palette.text.secondary, 
                    fontFamily: 'monospace',
                    fontSize: '0.95rem',
                    backgroundColor: palette.grey[50],
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: `1px solid ${palette.grey[200]}`
                  }}>
                    {faq.slug}
                  </Typography>
                </Box> */}
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Status
                  </Typography>
                  <Chip
                    label={faq.status?.charAt(0).toUpperCase() + faq.status?.slice(1)}
                    color={getStatusColor(faq.status)}
                    size="small"
                    sx={{
                      fontFamily: 'poppinsMedium',
                      fontSize: '0.75rem',
                      width: 'fit-content'
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Description and Answer in same row */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                mb: 2,
                color: palette.primary.titledark
              }}
            >
              Content
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Short Answer
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${palette.divider}`,
                      flex: 1,
                      minHeight: '120px',
                      overflow: 'auto'
                    }}
                  >
                    {renderHTMLContent(faq.description, 'No description provided')}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Answer
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      border: `1px solid ${palette.divider}`,
                      flex: 1,
                      minHeight: '120px',
                      overflow: 'auto'
                    }}
                  >
                    {renderHTMLContent(faq.answer, 'No answer provided')}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* SEO Information - 2 items per row */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                mb: 2,
                color: palette.primary.titledark
              }}
            >
              SEO Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Meta Title
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: palette.text.secondary,
                      fontSize: '0.95rem'
                    }}
                  >
                    {faq.meta_title || 'Not set'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Meta Description
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: palette.text.secondary,
                      fontSize: '0.95rem',
                      lineHeight: 1.5
                    }}
                  >
                    {faq.meta_description || 'Not set'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Background Media - Centered */}
          {faq.bg_video && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'poppinsSemiBold',
                    mb: 2,
                    color: palette.primary.titledark
                  }}
                >
                  Background Media
                </Typography>
                <Box>
                  {getFileTypeFromUrl(faq.bg_video) === 'image' ? (
                    <img
                      src={faq.bg_video}
                      alt="FAQ Background"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        border: `1px solid ${palette.divider}`,
                        display: 'block',
                        margin: '0 auto'
                      }}
                    />
                  ) : getFileTypeFromUrl(faq.bg_video) === 'video' ? (
                    <video
                      controls
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        border: `1px solid ${palette.divider}`,
                        display: 'block',
                        margin: '0 auto'
                      }}
                    >
                      <source src={faq.bg_video} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                      Unsupported file type
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Timestamps - 2 items per row */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'poppinsSemiBold',
                mb: 2,
                color: palette.primary.titledark
              }}
            >
              Timestamps
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Created Date
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: palette.text.secondary,
                      backgroundColor: palette.grey[50],
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      border: `1px solid ${palette.grey[200]}`
                    }}
                  >
                    {formatDate(faq.created_at)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      color: palette.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Last Updated
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: palette.text.secondary,
                      backgroundColor: palette.grey[50],
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      border: `1px solid ${palette.grey[200]}`
                    }}
                  >
                    {formatDate(faq.updated_at)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewFaqModal;
